// pages/Testimonials.jsx
import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../api/client';

const Testimonials = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        location: '',
        text: '',
        rating: 5
    });
    const [artworkImage, setArtworkImage] = useState(null);
    const [submitStatus, setSubmitStatus] = useState('idle'); // idle | submitting | success | error
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        const loadTestimonials = async () => {
            try {
                const data = await apiGet('/testimonials');
                setTestimonials(data);
            } catch (err) {
                setError('Unable to load testimonials right now.');
            } finally {
                setLoading(false);
            }
        };

        loadTestimonials();
    }, []);

    // Generate star rating display
    const renderStars = (rating, onClick) => {
        let stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <i
                    key={i}
                    className={`fas fa-star ${i <= rating ? 'filled' : ''}`}
                    onClick={onClick ? () => onClick(i) : undefined}
                    style={{
                        color: i <= rating ? 'var(--secondary-color)' : '#e0e0e0',
                        marginRight: '3px',
                        cursor: onClick ? 'pointer' : 'default',
                        fontSize: onClick ? '1.5rem' : undefined
                    }}
                ></i>
            );
        }
        return stars;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleRatingChange = (rating) => {
        setFormData({ ...formData, rating });
    };

    const handleFileChange = (e) => {
        setArtworkImage(e.target.files[0] || null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('submitting');
        setSubmitError('');

        try {
            const data = new FormData();
            data.append('name', formData.name);
            if (formData.location) data.append('location', formData.location);
            data.append('text', formData.text);
            data.append('rating', formData.rating);
            if (artworkImage) data.append('artworkImage', artworkImage);

            await apiPost('/testimonials', data, true);
            setSubmitStatus('success');
            setFormData({ name: '', location: '', text: '', rating: 5 });
            setArtworkImage(null);
        } catch (err) {
            setSubmitStatus('error');
            setSubmitError('There was an error submitting your testimonial. Please try again.');
        }
    };

    return (
        <div className="container">
            <div className="section-header" style={{ textAlign: 'center', marginBottom: '3rem' }}>
                <h2 className="section-title">Client Testimonials</h2>
                <p style={{ maxWidth: '800px', margin: '0 auto', fontSize: '1.1rem' }}>
                    Hear from collectors, galleries, and art enthusiasts who have experienced
                    Samir Dutta's artistic vision firsthand.
                </p>
            </div>

            {loading && (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                    <p>Loading testimonials...</p>
                </div>
            )}

            {error && (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                    <p>{error}</p>
                </div>
            )}

            {/* Testimonials Grid */}
            {!loading && !error && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: '2rem',
                    margin: '2rem 0 4rem'
                }}>
                    {testimonials.map((testimonial) => (
                        <div key={testimonial.id} className="testimonial-card slide-up" style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
                            transition: 'transform 0.3s ease',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {testimonial.artworkImageUrl && (
                                <div style={{
                                    height: '200px',
                                    overflow: 'hidden'
                                }}>
                                    <img
                                        src={testimonial.artworkImageUrl}
                                        alt={`Artwork for ${testimonial.name}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </div>
                            )}
                            <div style={{
                                padding: '1.5rem',
                                flex: '1',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{ marginBottom: '1rem' }}>
                                    {renderStars(testimonial.rating)}
                                </div>

                                <p style={{
                                    fontStyle: 'italic',
                                    marginBottom: '1.5rem',
                                    flex: '1'
                                }}>"{testimonial.text}"</p>

                                <div style={{
                                    marginTop: 'auto',
                                    borderTop: '1px solid #eee',
                                    paddingTop: '1rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: 'var(--primary-color)',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '1rem'
                                    }}>
                                        <span style={{ fontSize: '1.2rem' }}>
                                            {testimonial.name.charAt(0)}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 style={{ margin: '0', fontSize: '1rem' }}>{testimonial.name}</h4>
                                        {testimonial.location && (
                                            <p style={{ margin: '0', fontSize: '0.9rem', color: 'var(--accent-color)' }}>
                                                {testimonial.location}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {testimonials.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '2rem 0', gridColumn: '1 / -1' }}>
                            <p>No testimonials to show yet.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Submit Testimonial Form */}
            <div className="section" style={{
                textAlign: 'center',
                margin: '0 0 2rem',
                padding: '2rem',
                backgroundColor: 'rgba(51, 92, 103, 0.05)',
                borderRadius: '8px'
            }}>
                <h3 style={{ color: 'var(--primary-color)', marginBottom: '1rem' }}>Share Your Experience</h3>
                <p style={{ maxWidth: '700px', margin: '0 auto 1.5rem' }}>
                    If you've purchased or commissioned artwork from Samir Dutta, we'd love to hear about your experience.
                </p>

                {submitStatus === 'success' && (
                    <div className="success-message" style={{
                        backgroundColor: 'rgba(62, 142, 87, 0.1)',
                        color: '#3e8e57',
                        padding: '1rem',
                        borderRadius: '4px',
                        marginBottom: '1.5rem',
                        borderLeft: '4px solid #3e8e57',
                        maxWidth: '600px',
                        margin: '0 auto 1.5rem',
                        textAlign: 'left'
                    }}>
                        <i className="fas fa-check-circle" style={{ marginRight: '0.5rem' }}></i>
                        Thank you! Your testimonial has been submitted and is pending review.
                    </div>
                )}

                {submitStatus === 'error' && (
                    <div className="error-message" style={{
                        backgroundColor: 'rgba(220, 53, 69, 0.1)',
                        color: '#dc3545',
                        padding: '1rem',
                        borderRadius: '4px',
                        marginBottom: '1.5rem',
                        borderLeft: '4px solid #dc3545',
                        maxWidth: '600px',
                        margin: '0 auto 1.5rem',
                        textAlign: 'left'
                    }}>
                        <i className="fas fa-exclamation-circle" style={{ marginRight: '0.5rem' }}></i>
                        {submitError}
                    </div>
                )}

                <form onSubmit={handleSubmit} style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'left' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                        <div className="form-group">
                            <label className="form-label">Your Name *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="form-input"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Location</label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleChange}
                                className="form-input"
                                placeholder="e.g. Art Collector, Delhi"
                            />
                        </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                        <label className="form-label">Your Testimonial *</label>
                        <textarea
                            name="text"
                            value={formData.text}
                            onChange={handleChange}
                            className="form-textarea"
                            required
                            rows="4"
                        ></textarea>
                    </div>

                    <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                        <label className="form-label">Rating</label>
                        <div>
                            {renderStars(formData.rating, handleRatingChange)}
                        </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                        <label className="form-label">Artwork Image (optional)</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="form-input"
                        />
                    </div>

                    <button type="submit" className="btn" disabled={submitStatus === 'submitting'}>
                        {submitStatus === 'submitting' ? 'Submitting...' : 'Submit Your Testimonial'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Testimonials;
